import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { kebabCase } from 'lodash';
import type {
  Brand,
  Collection,
  DataLoaders,
  Dispensary,
  LoyaltyData,
  Product as RemoteProduct,
  ProductFilters,
  ProductRange,
  ProductTag as RemoteProductTag,
  Special,
  ProductImage as RemoteProductImage,
  ProductPotency as RemoteProductPotency,
} from '@dutchiesdk/ecommerce-extensions-sdk';
import { useRouter } from 'next/compat/router';
import { useApolloClient, useQuery } from '@apollo/client';

import {
  BridgeFilteredProductsDocument,
  GetCollectionDocument,
  GetCollectionsDocument,
  GetRemoteIntegrationValueDocument,
  IndividualFilteredProductDocument,
} from 'src/gql/graphql';
import { Product } from 'types/graphql-overrides';
import type {
  Brand as BrandGql,
  BridgeFilteredProductsQuery,
  Collection as CollectionGql,
  Dispensaries,
  GetCollectionQuery,
  GetRemoteIntegrationValueQuery,
  IndividualFilteredProductQuery,
  Specials,
} from 'src/gql/graphql';
import useCurrentDispensary from 'src/dispensary/hooks/use-current-dispensary';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import useMenuFilterOptions, { menuFilters } from 'hooks/use-menu-filter-options';
import useDispensarySpecials from 'src/dispensary/hooks/use-dispensary-specials';
import useDispensaryProducts, { getProductsVariables } from 'src/dispensary/hooks/use-dispensary-products';
import type { DispensaryMenuCategory } from 'src/dispensary/hooks/use-dispensary-categories';
import { useDispensaryChains } from 'src/dispensary/hooks/use-dispensary-chain';
import { StrainTypeFilterOptions } from 'shared/constants';
import { WhenNotVoid } from 'shared/utils/type-utils';
import { mapOrderTypeToPreOrderType } from 'shared/helpers/enum-mappings';
import { getVariables } from 'src/hooks/use-product';
import useUI from 'src/hooks/use-ui';
import { useCollectionChannel } from 'src/hooks/use-collection-channel';
import { useCatalogRequestContext } from 'src/contexts/catalog-request-context';
import { useUserSegments } from 'src/hooks/use-user-segments';
import { useLoyalty, UseLoyaltyReturn } from 'src/components/widgets/loyalty-tiers/use-loyalty';
import { replaceAWSSourceWithImgix } from 'shared/helpers/imgix';
import { parseProduct } from 'utils/helpers/product';
import { createMapCategoryToRemote } from './bridge-map-category';
import { mapDispensaryToRemote } from './bridge-map-dispensary';

// TEMPORARY: Local shims for smart tags until @dutchiesdk/ecommerce-extensions-sdk exports them (ECOMM-12637)
export type SmartTag = {
  id: string;
  name: string;
};

export type ProductWithSmartTags = RemoteProduct & {
  smartTags?: SmartTag[];
};

export type BridgeDataLoaders = Omit<DataLoaders, 'product' | 'products'> & {
  products: (filters?: ProductFilters) => Promise<ProductWithSmartTags[]>;
  product: () => Promise<ProductWithSmartTags | null>;
};

export const resolveBrandIdsByCname = (brandCname: string, brandsList?: BrandGql[]): string[] => {
  if (!brandCname || !brandsList || brandsList.length === 0) {
    return [];
  }
  const normalizedTarget = brandCname.toLowerCase();

  return brandsList
    .filter((brand) => brand.name && kebabCase(brand.name).toLowerCase() === normalizedTarget)
    .map((brand) => brand.id);
};

export const normalizeStrainTypes = (strainType?: string[] | string): string[] | undefined => {
  if (strainType === undefined) {
    return undefined;
  }
  const rawList = Array.isArray(strainType) ? strainType : [strainType];
  const list = rawList.filter((s) => typeof s === 'string' && s.trim().length > 0);
  if (list.length === 0) {
    return undefined;
  }

  return list.map((item) => {
    const trimmed = item.trim();
    const matched = StrainTypeFilterOptions.find(
      (opt) =>
        opt.key.toLowerCase() === trimmed.toLowerCase() ||
        opt.value.toLowerCase() === trimmed.toLowerCase() ||
        opt.label.toLowerCase() === trimmed.toLowerCase()
    );
    return matched ? matched.value : trimmed;
  });
};

export const validatePriceRange = (priceRange?: ProductRange): boolean => {
  if (!priceRange) return false;
  const { min, max } = priceRange;
  if (min === undefined && max === undefined) return false;
  if (min !== undefined && (typeof min !== 'number' || !Number.isFinite(min) || min < 0)) {
    return false;
  }
  if (max !== undefined && (typeof max !== 'number' || !Number.isFinite(max) || max < 0)) {
    return false;
  }
  if (min !== undefined && max !== undefined && min > max) {
    return false;
  }
  return true;
};

export const reconcileWeightAndOption = (
  weight?: string,
  option?: string
): { option?: string; hasConflict: boolean } => {
  if (weight !== undefined && option !== undefined && weight !== option) {
    return { hasConflict: true };
  }
  return { option: weight ?? option, hasConflict: false };
};

export const hasProductFilters = (filters?: ProductFilters): filters is ProductFilters => {
  if (!filters) {
    return false;
  }

  if (
    filters.category ||
    filters.collection ||
    filters.option ||
    filters.brandCname ||
    filters.weight ||
    filters.priceRange !== undefined
  ) {
    return true;
  }

  if (filters.strainType) {
    if (Array.isArray(filters.strainType)) {
      if (filters.strainType.length > 0) return true;
    } else if (filters.strainType.trim().length > 0) {
      return true;
    }
  }

  return false;
};

const resolveCategoryFilter = (
  categoryFilter: string,
  categoryList: DispensaryMenuCategory[]
): DispensaryMenuCategory | undefined => {
  const normalized = categoryFilter.toLowerCase();

  return (
    categoryList.find((category) => category.key.toLowerCase() === normalized) ??
    categoryList.find((category) => category.slug?.toLowerCase() === normalized)
  );
};

type ResolvablePromise<T> = Promise<T> & {
  resolve?: (value: T) => void;
};

const createResolvablePromise = <T>(): ResolvablePromise<T> => {
  let outerResolve: ((value: T) => void) | null = null;
  const promise: ResolvablePromise<T> = new Promise<T>((resolve) => {
    outerResolve = resolve;
  });
  // The Promise constructor invokes its callback synchronously.
  promise.resolve = (outerResolve as unknown) as (value: T) => void;
  return promise;
};

function useCreateLazyDataLoader<T>(
  useData: (skip: boolean) => { data: T; loading: boolean },
  dependencies: any[] = []
): { data: T; dataLoader: () => Promise<T> } {
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const resolvablePromise = useMemo<ResolvablePromise<T>>(
    () => createResolvablePromise<T>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...dependencies]
  );
  const refreshPromise = useRef<ResolvablePromise<T> | undefined>();

  const { data, loading } = useData(!shouldFetch);

  useEffect(() => {
    if (!shouldFetch || loading) {
      return;
    }

    if (resolvablePromise.resolve) {
      const { resolve } = resolvablePromise;
      delete resolvablePromise.resolve;
      resolve(data);
    }

    if (refreshPromise.current?.resolve) {
      refreshPromise.current.resolve(data);
      refreshPromise.current = undefined;
    }
  }, [data, loading, shouldFetch, resolvablePromise]);

  const dataLoader = useCallback(async () => {
    setShouldFetch(true);

    if (resolvablePromise.resolve) {
      return resolvablePromise;
    }
    if (!loading) {
      return data;
    }

    refreshPromise.current ??= createResolvablePromise<T>();
    return refreshPromise.current;
  }, [data, loading, resolvablePromise]);

  return { data, dataLoader };
}

export const useBridgeDataLoaders = (): BridgeDataLoaders => {
  const apolloClient = useApolloClient();
  const { dispensary: currentDispensary, loading: currentDispensaryLoading } = useCurrentDispensary();
  const { dispensary: legacyDispensary, loading: legacyDispensaryLoading } = useDispensary() as {
    dispensary?: Dispensaries | undefined;
    loading?: boolean;
  };
  const dispensary = currentDispensary ?? legacyDispensary;
  // True only while dispensary resolution is still in flight and neither source has answered yet.
  // Once both sources finish (e.g. a genuinely nonexistent dispensary), this goes false even though
  // `dispensary` stays unresolved, so callers waiting on it are not blocked forever (ECOMM-13997).
  const dispensaryResolving = !dispensary?.id && Boolean(currentDispensaryLoading || legacyDispensaryLoading);
  const UI = useUI();
  const channel = useCollectionChannel();
  const { categories } = useDispensaryCategories();
  const { brands } = useMenuFilterOptions({ skipCurrentFilters: true });
  const router = useRouter();
  const { query } = router ?? {};
  const { isDemo, isKiosk, isEmbedded, isStoreFront, isMobileEcommApp } = UI;
  const { menuType, orderType } = useCatalogRequestContext();
  const { segments: userSegments } = useUserSegments({ dispensaryId: dispensary?.id });

  // Expensive Data Loaders - Use lazy loading to only load when requested.

  // SPECIALS
  const { dataLoader: getAllSpecials } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { specials, loading } = useDispensarySpecials({
      skip,
    });

    return { data: specials, loading: skip || loading };
  });

  // PRODUCTS
  const { dataLoader: getAllProducts } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { products, loading } = useDispensaryProducts({
      skip,
    });

    return { data: products, loading: skip || loading };
  });

  const getFilteredProducts = useCallback(
    async (filters: ProductFilters): Promise<Product[]> => {
      let types: string[] = [];
      let collectionId: string | undefined;

      if (filters.category) {
        const resolvedCategory = resolveCategoryFilter(filters.category, categories);

        if (!resolvedCategory) {
          return [];
        }

        if (resolvedCategory.customCategory) {
          collectionId = resolvedCategory.key;
        } else {
          types = [resolvedCategory.value];
        }
      }

      if (filters.collection) {
        const { data } = await apolloClient.query<Partial<GetCollectionQuery>>({
          query: GetCollectionDocument,
          variables: {
            collectionId: filters.collection,
            dispensaryId: dispensary?.id ?? '',
            ...(channel && { channel }),
          },
          errorPolicy: 'all',
          fetchPolicy: 'network-only',
        });
        const resolvedCollection = (data as Partial<GetCollectionQuery> | undefined)?.getCollection;

        if (
          !resolvedCollection?.id ||
          (resolvedCollection.isExclusive && !resolvedCollection.isAccessibleToCurrentUser) ||
          (collectionId && collectionId !== resolvedCollection.id)
        ) {
          return [];
        }

        collectionId = resolvedCollection.id;
      }

      const { option, hasConflict: optionConflict } = reconcileWeightAndOption(filters.weight, filters.option);
      if (optionConflict) {
        return [];
      }

      let brandIds: string[] | undefined;
      if (filters.brandCname) {
        let availableBrands = brands;
        if (((brands as BrandGql[] | undefined)?.length ?? 0) === 0 && dispensary?.id) {
          const { data: filterData } = await apolloClient.query<{ menuFiltersV2?: { brands?: BrandGql[] } }>({
            query: menuFilters,
            variables: {
              dispensaryId: dispensary.id,
              pricingType: menuType,
              isKioskMenu: isKiosk,
            },
            errorPolicy: 'all',
          });
          const fetchedBrands = (filterData as { menuFiltersV2?: { brands?: BrandGql[] } } | undefined)?.menuFiltersV2
            ?.brands;
          availableBrands = fetchedBrands ?? [];
        }

        const resolvedBrandIds = resolveBrandIdsByCname(filters.brandCname, availableBrands);
        if (resolvedBrandIds.length === 0) {
          return [];
        }
        brandIds = resolvedBrandIds;
      }

      const strainTypes = normalizeStrainTypes(filters.strainType);

      let priceRange: { min?: number; max?: number } | undefined;
      if (filters.priceRange) {
        if (!validatePriceRange(filters.priceRange)) {
          return [];
        }
        priceRange = {
          ...(filters.priceRange.min !== undefined && { min: filters.priceRange.min }),
          ...(filters.priceRange.max !== undefined && { max: filters.priceRange.max }),
        };
      }

      const variables = getProductsVariables({
        dispensaryId: dispensary?.id,
        pricingType: menuType,
        isKiosk,
        UI,
        preOrderType: mapOrderTypeToPreOrderType(orderType),
        userSegments,
        types,
        collectionId,
        option,
        brandIds,
        strainTypes,
        priceRange,
        page: 1,
        perPage: 100,
      });

      const { data } = await apolloClient.query<BridgeFilteredProductsQuery>({
        query: BridgeFilteredProductsDocument,
        variables,
      });

      const resolvedProducts = (data as Partial<BridgeFilteredProductsQuery> | undefined)?.filteredProducts?.products;
      return (resolvedProducts ?? []).map(parseProduct);
    },
    [apolloClient, brands, categories, channel, dispensary?.id, isKiosk, menuType, orderType, UI, userSegments]
  );

  const { dataLoader: getProduct } = useCreateLazyDataLoader(
    (skip) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { data: individualFilteredProductData, loading } = useQuery(IndividualFilteredProductDocument, {
        variables: getVariables(query?.productId, dispensary, UI.isKiosk, UI),
        // Non-Active reads must be scoped to a dispensary (ECOMM-13997); wait until it has resolved,
        // same as hooks/use-product.js -- otherwise this fires with dispensaryId undefined and the
        // policy guard in internalFilteredProducts rejects it with ForbiddenError.
        skip: skip || !query?.productId || !dispensary?.id,
      });

      // Reporting `loading: false` while dispensary is still resolving would let
      // useCreateLazyDataLoader's effect resolve getProduct()'s promise to `undefined` right away
      // (Apollo reports loading: false for a skipped query) -- the caller gets a premature `null`
      // instead of actually waiting, reproducing the same empty-PDP symptom this guard exists to
      // fix, just silently. Folding dispensaryResolving in here keeps the promise pending until
      // dispensary resolution genuinely finishes one way or the other.
      return {
        data: individualFilteredProductData?.filteredProducts?.products[0],
        loading: skip || loading || dispensaryResolving,
      };
    },
    [query?.productId]
  );

  // LOCATIONS
  const { dataLoader: getAllLocations } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { dispensaryChainLocations, loading } = useDispensaryChains({
      activeOnly: true,
      includeStealthMode: true,
      skip,
    });

    return { data: dispensaryChainLocations ?? [], loading: skip || loading };
  });

  // COLLECTIONS
  const { dataLoader: getAllCollections } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: collectionsData, loading } = useQuery(GetCollectionsDocument, {
      skip: skip || !dispensary?.id,
      variables: {
        dispensaryId: dispensary?.id ?? '',
        customCategory: false,
        activeOnly: true,
        ...(channel && { channel }),
      },
    });

    return { data: collectionsData?.getCollections ?? [], loading: skip || loading };
  });

  // INTEGRATION VALUE
  const getIntegrationValue = useCallback(
    async (key = ''): Promise<GetRemoteIntegrationValueQuery | undefined> => {
      const result = await apolloClient.query({
        query: GetRemoteIntegrationValueDocument,
        variables: {
          dispensaryId: dispensary?.id ?? '',
          key,
        },
      });
      return result.data;
    },
    [apolloClient, dispensary?.id]
  );

  // LOYALTY
  const { dataLoader: getLoyalty } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { loading, ...data } = useLoyalty(undefined, undefined, undefined, { skip });

    return { data, loading: skip || loading };
  }, []);

  const mapCategoryToRemote = createMapCategoryToRemote({
    isDemo,
    isKiosk,
    isEmbedded,
    isStoreFront,
    isMobileEcommApp,
    dispensary: dispensary ?? undefined,
  });

  return {
    brands: async () => {
      const result = await Promise.resolve(brands);
      return result.map(mapBrandToRemote);
    },
    categories: async () => {
      const result = await Promise.resolve(categories);
      return result.map(mapCategoryToRemote);
    },
    collections: async () => {
      const result = await getAllCollections();
      return result.map(mapCollectionToRemote);
    },
    integrationValue: async (key: string) => {
      const result = await getIntegrationValue(key);
      return result?.getRemoteIntegrationValue ?? undefined;
    },
    locations: async () => {
      const result = await getAllLocations();
      return result.map(mapDispensaryToRemote).filter((d): d is Dispensary => d !== undefined);
    },
    product: async () => {
      const result = await getProduct();
      return result ? mapProductToRemote(result) : null;
    },
    products: async (filters?: ProductFilters) => {
      if (!hasProductFilters(filters)) {
        const result = await getAllProducts();
        return result.map(mapProductToRemote);
      }

      const result = await getFilteredProducts(filters);
      return result.map(mapProductToRemote);
    },
    specials: async () => {
      const result = await getAllSpecials();
      return result.map(mapSpecialToRemote);
    },
    loyalty: async () => {
      const result = await getLoyalty();
      // Per the SDK contract, loyalty() returns null when the user isn't logged in or loyalty
      // isn't configured/applicable for this dispensary -- loyaltyWidgetEnabled already
      // encapsulates both of those conditions (see useLoyalty's shouldShowLoyaltyWidget).
      return result.loyaltyWidgetEnabled ? mapLoyaltyToRemote(result) : null;
    },
  };
};

const mapBrandToRemote = (brand: BrandGql): Brand => ({
  cname: kebabCase(brand.name),
  description: brand.description ?? undefined,
  id: brand.id,
  image: brand.imageUrl ?? '',
  name: brand.name,
});

const mapCollectionToRemote = (collection: CollectionGql): Collection => ({
  cname: collection.slug,
  id: collection.id,
  name: collection.title,
  position: collection.position ?? undefined,
  cardBadgeColor: collection.cardBadgeColor ?? undefined,
  cardBadgeTitle: collection.cardBadgeTitle ?? undefined,
  cardImage: collection.cardImage ?? undefined,
  description: collection.description ?? undefined,
  image: collection.image ?? undefined,
  isExclusive: collection.isExclusive ?? undefined,
  isOutlet: collection.isOutlet ?? undefined,
  navigationEnabled: collection.navigationEnabled,
  navigationTitle: collection.navigationTitle ?? undefined,
  outletTimerMessage: collection.outletTimerMessage ?? undefined,
  productCount: collection.productCount,
});

type MappableProduct =
  | Product
  | WhenNotVoid<WhenNotVoid<IndividualFilteredProductQuery['filteredProducts']>['products'][number]>;

const filterNulls = <T>(values?: (T | null)[] | null): T[] | undefined =>
  values?.filter((value): value is T => value != null);

const mapPotencyToRemote = (
  potency?: { range?: (number | null)[] | null; unit: string } | null
): RemoteProductPotency | undefined =>
  potency ? { range: filterNulls(potency.range), unit: potency.unit } : undefined;

const mapImagesToRemote = (
  images?: { url: string; description?: string | null; active: boolean }[] | null
): RemoteProductImage[] | undefined =>
  images
    ?.filter((image) => image.active)
    .map((image) => ({ url: image.url, description: image.description ?? undefined }));

type RawTagInput = ({ tagId?: string | null; tagName?: string | null } | null)[] | null | undefined;

const mapTagsToRemote = <T extends { id: string; name: string } = RemoteProductTag>(
  tags?: RawTagInput
): T[] | undefined => {
  const mapped = tags?.flatMap((tag) => (tag?.tagId && tag.tagName ? [{ id: tag.tagId, name: tag.tagName } as T] : []));
  return mapped?.length ? mapped : undefined;
};

const mapProductToRemote = (product: MappableProduct): ProductWithSmartTags => {
  const prices = filterNulls((product as Product).prices ?? product.Prices);

  return {
    brandId: product.brandId ?? undefined,
    brandName: product.brandName ?? undefined,
    category: product.type ?? undefined,
    cbdContent: mapPotencyToRemote(product.CBDContent),
    cname: product.cName ?? product.id ?? '',
    comingSoon: product.comingSoon ?? undefined,
    description: product.description ?? '',
    effects: product.effects ?? undefined,
    featured: product.featured?.current ?? undefined,
    id: product.id ?? '',
    image: product.Image ?? '',
    images: mapImagesToRemote(product.images),
    medicalOnly: product.medicalOnly ?? undefined,
    medicalPrices: filterNulls(product.medicalPrices),
    medicalSpecialPrices: filterNulls(product.medicalSpecialPrices),
    name: product.Name ?? '',
    options: filterNulls(product.Options),
    price: prices?.[0] ?? 0,
    prices,
    recOnly: product.recOnly ?? undefined,
    recPrices: filterNulls(product.recPrices),
    recSpecialPrices: filterNulls(product.recSpecialPrices),
    smartTags: mapTagsToRemote<SmartTag>(product.POSMetaData?.activeBatchTags),
    strainType: product.strainType ?? undefined,
    subcategory: product.subcategory ?? undefined,
    tags: mapTagsToRemote(product.POSMetaData?.canonicalProductTags),
    terpenes: (product.terpenes ?? [])
      .filter((t) => !/total\s+terpenes/i.test(t?.libraryTerpene?.name ?? ''))
      .map((t) => ({
        name: t?.libraryTerpene?.name ?? '',
        unit: t?.unitSymbol ?? t?.unit ?? undefined,
        value: t?.value ?? undefined,
      })),
    thcContent: mapPotencyToRemote(product.THCContent),
    weight: product.weight ?? undefined,
  };
};

const resolveSpecialImage = (menuDisplayImage?: string | null): string => {
  const normalized = replaceAWSSourceWithImgix(menuDisplayImage);
  return normalized.includes('://') ? normalized : '';
};

const resolveSpecialDate = (stamp: Date | string | null | undefined): string | undefined => {
  if (!stamp) {
    return undefined;
  }
  const date = stamp instanceof Date ? stamp : new Date(stamp);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const mapSpecialToRemote = (special: Specials): Special => ({
  cname: kebabCase(special.name ?? ''),
  description: special.menuDisplayDescription ?? '',
  descriptionHtml: special.descriptionHtml ?? undefined,
  displayRank: special.displayRank ?? undefined,
  endDate: resolveSpecialDate(special.endStamp),
  heading: special.heading ?? undefined,
  id: special._id ?? '',
  image: resolveSpecialImage(special.menuDisplayImage),
  isRecurring: special.isRecurring ?? undefined,
  menuType: special.menuType ?? undefined,
  name: special.name ?? '',
  startDate: resolveSpecialDate(special.startStamp),
  type: special.specialType ?? undefined,
});

const mapLoyaltyToRemote = (loyaltyData: Omit<UseLoyaltyReturn, 'loading'>): LoyaltyData => ({
  balance: loyaltyData.currentBalance.toString(),
  currentTierName: loyaltyData.currentTierName ?? '',
  currentTierThreshold: loyaltyData.currentTierThreshold ?? 0,
  nextTierName: loyaltyData.nextTierName ?? '',
  nextTierThreshold: loyaltyData.targetBalance,
  expirationDate: loyaltyData.expirationDate ?? null,
  rewardsProgramName: loyaltyData.rewardsProgramName,
  balanceDaysToExpiration: loyaltyData.balanceDaysToExpiration ?? null,
  badgeColor: loyaltyData.badgeColor,
  progressPercentage: loyaltyData.progressPercentageToNextTier,
  isLoyaltyMember: loyaltyData.isLoyaltyMember,
  isPoints: loyaltyData.isPoints,
  isDiscountRedemption: loyaltyData.isDiscountRedemption,
});
